import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    // null = checking, false = unauthenticated, object = user
    const [user, setUser] = useState(null);

    useEffect(() => {
        let cancel = false;
        (async () => {
            try {
                const { data } = await api.get("/auth/me");
                if (!cancel) setUser(data);
            } catch {
                if (!cancel) setUser(false);
            }
        })();
        return () => {
            cancel = true;
        };
    }, []);

    const login = useCallback(async (email, password) => {
        try {
            const { data } = await api.post("/auth/login", { email, password });
            setUser(data);
            return { ok: true };
        } catch (e) {
            return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
        }
    }, []);

    const register = useCallback(async (email, password, name) => {
        try {
            const { data } = await api.post("/auth/register", { email, password, name });
            setUser(data);
            return { ok: true };
        } catch (e) {
            return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
        }
    }, []);

    const logout = useCallback(async () => {
        try {
            await api.post("/auth/logout");
        } catch (err) {
            // non-fatal: server-side session is best-effort; we still clear local state below
            console.error("logout failed:", err);
        }
        setUser(false);
    }, []);

    const value = useMemo(
        () => ({ user, setUser, login, register, logout }),
        [user, login, register, logout],
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
    return useContext(AuthContext);
}
